import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ProductDataListParameter } from '@shared/interfaces/data-list-parameter.interface';
import { throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  Product,
  ProductRequestBody,
} from 'src/app/features/product/interfaces/product.interface';
import { environment } from 'src/environments/environment';
import { Filter, Sort } from '../../../shared/interfaces/common.interface';

const apiUrl = environment.apiUrl + 'guest';

@Injectable({
  providedIn: 'root',
})
export class ProductService {
  constructor(private http: HttpClient, private router: Router) {}

  getProducts(productDataListParameter: ProductDataListParameter) {
    // note: is filtered items  needed to be added?
    return this.http
      .post(apiUrl + '/products', productDataListParameter)
      .pipe(
        map((res: any) => {
          let products: Product[] = res.data.products;
          products.forEach(
            (x) => (x.average_rating = Number(x.average_rating))
          );
          return {
            totalProducts: res.data.count,
            products: products,
            filteredItems: res.data.body,
          };
        })
      )
      .pipe(
        catchError((error) => {
          return throwError(error);
        })
      );
  }

  searchProducts(
    itemPerPage: number,
    currentPage: number = 1,
    searchQuery: string
  ) {
    return this.http
      .post(apiUrl + '/products', {
        item_per_page: itemPerPage,
        current_page: currentPage,
        search_query: searchQuery,
      })
      .pipe(
        map((res: any) => {
          let products: Product[] = res.data.products;
          products.forEach(
            (x) => (x.average_rating = Number(x.average_rating))
          );
          return { totalProducts: res.data.count, products: products };
        })
      )
      .pipe(
        catchError((error) => {
          return throwError(error);
        })
      );
  }

  // note: need to put this on wishlist service
  getWishlistedProducts(
    itemPerPage: number,
    currentPage: number = 1,
    filters?: Filter[],
    sorts?: Sort[]
  ) {
    return this.http
      .post(apiUrl + '/products', {
        item_per_page: itemPerPage,
        current_page: currentPage,
        filters: filters,
        sorts: sorts,
        wishlisted: true,
      })
      .pipe(
        map((res: any) => {
          let products: Product[] = res.data.products;
          products.forEach(
            (x) => (x.average_rating = Number(x.average_rating))
          );
          return { totalProducts: res.data.count, products: products };
        })
      )
      .pipe(
        catchError((error) => {
          return throwError(error);
        })
      );
  }

  getProductsWithFilters(body: ProductRequestBody) {
    return this.http
      .post(apiUrl + '/products', {
        item_per_page: body.itemPerPage,
        current_page: body.currentPage,
        filters: body.filters,
        sorts: body.sorts,
        shop_slug: body.shopSlug,
        category_id: body.categoryId,
        category_slug: body.categorySlug,
        brands: body.brands,
        min_price: body.minPrice,
        max_price: body.maxPrice,
        search_query: body.searchQuery,
        show_promotion_only: body.showPromotionOnly,
        foundation_shade_colors: body.foundationShadeColors,
      })
      .pipe(
        map((res: any) => {
          let products: Product[] = res.data.product;
          products.forEach(
            (x) => (x.average_rating = Number(x.average_rating))
          );
          return {
            totalProducts: res.data.count,
            products: products,
            body: res.data.body,
          };
        })
      )
      .pipe(catchError((error) => this.handleError(error)));
  }

  getBestSellerProducts(
    itemPerPage: number,
    currentPage: number = 1,
    filters?: Filter[],
    sorts?: Sort[]
  ) {
    const body = {
      item_per_page: itemPerPage,
      current_page: currentPage,
      filters: filters,
      sorts: sorts,
    };
    return this.http
      .post(apiUrl + '/best_sellers', body)
      .pipe(
        map((res: any) => {
          let products: Product[] = res.data.products;
          products.forEach(
            (x) => (x.average_rating = Number(x.average_rating))
          );
          return products;
        })
      )
      .pipe(
        catchError((error) => {
          return throwError(error);
        })
      );
  }

  getBestSellerProductsWithFilters(body: ProductRequestBody) {
    return this.http
      .post(apiUrl + '/best_sellers', {
        item_per_page: body.itemPerPage,
        current_page: body.currentPage,
        filters: body.filters,
        sorts: body.sorts,
        shop_slug: body.shopSlug,
        category_id: body.categoryId,
        category_slug: body.categorySlug,
        brands: body.brands,
        min_price: body.minPrice,
        max_price: body.maxPrice,
        search_query: body.searchQuery,
        show_promotion_only: body.showPromotionOnly,
        foundation_shade_colors: body.foundationShadeColors,
      })
      .pipe(
        map((res: any) => {
          let products: Product[] = res.data.products;
          products.forEach(
            (x) => (x.average_rating = Number(x.average_rating))
          );
          return {
            totalProducts: res.data.count,
            products: products,
            body: res.data.body,
          };
        })
      )
      .pipe(catchError((error) => this.handleError(error)));
  }

  getRecommendSimilarProducts(
    slug: string,
    itemPerPage: number,
    currentPage: number = 1,
    filters?: Filter[],
    sorts?: Sort[]
  ) {
    const body = {
      item_per_page: itemPerPage,
      current_page: currentPage,
      filters: filters,
      sorts: sorts,
    };
    return this.http
      .post(apiUrl + '/recommend_similar_products/' + slug, body)
      .pipe(
        map((res: any) => {
          let products: Product[] = res.data.products;
          products.forEach(
            (x) => (x.average_rating = Number(x.average_rating))
          );
          return products;
        })
      )
      .pipe(
        catchError((error) => {
          return throwError(error);
        })
      );
  }

  getMyRecommendationProducts(
    itemPerPage: number,
    currentPage: number = 1,
    filters?: Filter[],
    sorts?: Sort[]
  ) {
    return this.http
      .post(apiUrl + '/recommended_products', {
        item_per_page: itemPerPage,
        current_page: currentPage,
        filters: filters,
        sorts: sorts,
      })
      .pipe(
        map((res: any) => {
          let products: Product[] = res.data.products;
          products.forEach(
            (x) => (x.average_rating = Number(x.average_rating))
          );
          return { totalProducts: res.data.count, products: products };
        })
      )
      .pipe(
        catchError((error) => {
          return throwError(error);
        })
      );
  }

  getMyRecommendationProductsWithFilters(body: ProductRequestBody) {
    return this.http
      .post(apiUrl + '/recommended_products', {
        item_per_page: body.itemPerPage,
        current_page: body.currentPage,
        filters: body.filters,
        sorts: body.sorts,
        shop_slug: body.shopSlug,
        category_id: body.categoryId,
        category_slug: body.categorySlug,
        brands: body.brands,
        min_price: body.minPrice,
        max_price: body.maxPrice,
        search_query: body.searchQuery,
        show_promotion_only: body.showPromotionOnly,
        foundation_shade_colors: body.foundationShadeColors,
      })
      .pipe(
        map((res: any) => {
          let products: Product[] = res.data.products;
          products.forEach(
            (x) => (x.average_rating = Number(x.average_rating))
          );
          return {
            totalProducts: res.data.count,
            products: products,
            body: res.data.body,
          };
        })
      )
      .pipe(catchError((error) => this.handleError(error)));
  }

  // note: need to check
  private handleError(errorRes: HttpErrorResponse) {
    let errorMessage = 'Maaf terjadi kesalahan yang tidak diketahui';
    // if(errorRes) {
    //   let data = errorRes['data'];
    //   if(data.category_slug && data.category_slug[0] && data.category_slug[0] == "The selected category slug is invalid.") {
    //     this.router.navigate(['/error/not-found']);
    //   }
    // }
    return throwError(errorMessage);
  }
}
